/**
 * XRSessionPlayerPage — v10.8.0 (MVP stub)
 * Cliente XR mínimo: canjea el token temporal, muestra marcadores/zonas
 * configuradas y envía interacciones al backend. Sirve para validar el
 * flujo E2E sin necesidad de cámara AR real (la integración con MindAR /
 * WebXR llega en la próxima iteración).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Cube, MapPin, CheckCircle, WarningCircle, DeviceMobile, Heartbeat, ArrowsClockwise,
  Camera, CameraSlash, Target, ArrowsOut, ArrowsIn, Bug, Sliders, EyeSlash, Eye, DotsThree, X,
} from '@phosphor-icons/react';
import { createAdapter } from '../../xr/adapters';
import useXRCapabilities from '../../xr/hooks/useXRCapabilities';
import useXRPushNotifications from '../../xr/hooks/useXRPushNotifications';
import XRMarker3DOverlay from '../../xr/components/XRMarker3DOverlay';
import XRCalibrationPanel from '../../xr/components/XRCalibrationPanel';
import useXRMirror from '../../xr/hooks/useXRMirror';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DEVICE_TYPES = [
  { value: 'mobile_ar', label: 'Móvil AR' },
  { value: 'tablet_ar', label: 'Tablet AR' },
  { value: 'mixed_reality', label: 'Realidad Mixta' },
  { value: 'ar_glasses', label: 'Gafas AR' },
];

export default function XRSessionPlayerPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const caps = useXRCapabilities();

  const [phase, setPhase] = useState('choose'); // choose | redeeming | connected | error
  const [deviceType, setDeviceType] = useState('mobile_ar');
  const [session, setSession] = useState(null); // { access_token, session_id, scenario_id, user }
  const [state, setState] = useState(null); // patient_current_state
  const [scenarioName, setScenarioName] = useState('');
  const [xrConfig, setXrConfig] = useState({});
  const [markers, setMarkers] = useState([]);
  const [zones, setZones] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');

  // Push notifications (WS-relayed, mostradas por el SW). Solo activo cuando
  // ya hemos canjeado el token y tenemos access_token + session_id.
  const push = useXRPushNotifications({
    sessionId: session?.session_id,
    accessToken: session?.access_token,
    enabled: !!session?.access_token && !!session?.session_id,
  });

  // Refs must be declared before hooks that read their `.current`
  const videoRef = useRef(null);
  const adapterRef = useRef(null);
  const detectedSetRef = useRef(new Set()); // dedupe por marker_id + timestamp bucket

  // Mirror WebRTC: alumno publica su cámara al instructor si éste la solicita.
  const mirror = useXRMirror({
    role: 'student',
    sessionId: session?.session_id,
    accessToken: session?.access_token,
    videoEl: videoRef.current,
    enabled: !!session?.access_token && !!session?.session_id,
  });
  // v10.8.2 — AR camera con detección de marcadores por QR (compatible con
  // marcadores AR.js/MindAR estilo QR-encoded)
  const [cameraOn, setCameraOn] = useState(false);
  const [lastDetected, setLastDetected] = useState(null);
  const [cameraError, setCameraError] = useState('');
  // v10.12.0 — visor RA fullscreen + calibración
  const [activeMarker, setActiveMarker] = useState(null); // marker_id que está actualmente en frame
  const [markerBounds, setMarkerBounds] = useState(null); // {x,y,w,h,cx,cy} normalizado
  const [videoFrame, setVideoFrame] = useState({ videoWidth: 0, videoHeight: 0 });
  const [calibrations, setCalibrations] = useState({}); // marker_id -> calibration
  const [immersive, setImmersive] = useState(true); // player fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [calMode, setCalMode] = useState(false);
  const [ptCollapsed, setPtCollapsed] = useState(true);
  const rootRef = useRef(null);
  // v10.8.2 MVP Anatómico — secuencia + score + tiempo
  const [mvpState, setMvpState] = useState({ idx: 0, correct: 0, incorrect: 0, timeLeft: 0, finished: false });

  const redeem = async () => {
    setPhase('redeeming');
    try {
      const { data } = await axios.post(`${API_BASE}/api/xr/link/redeem`, {
        token,
        device_type: deviceType,
        device_name: navigator.userAgent.slice(0, 80),
      });
      setSession(data);
      setPhase('connected');
      // Fetch initial state + markers/zones
      const headers = { Authorization: `Bearer ${data.access_token}` };
      const [markRes, zoneRes] = await Promise.all([
        axios.get(`${API_BASE}/api/xr/markers${data.scenario_id ? `?scenario_id=${data.scenario_id}` : ''}`, { headers }),
        axios.get(`${API_BASE}/api/xr/zones`, { headers }),
      ]);
      setMarkers(markRes.data?.markers || []);
      setZones(zoneRes.data?.zones || []);
      if (data.session_id) {
        const st = await axios.get(`${API_BASE}/api/xr/sessions/${data.session_id}/state`, { headers });
        setState(st.data?.patient_current_state || {});
        setScenarioName(st.data?.scenario_name || '');
        const cfg = st.data?.xr_config || {};
        setXrConfig(cfg);
        if (cfg.mvp_type === 'anatomical_id') {
          setMvpState({ idx: 0, correct: 0, incorrect: 0, timeLeft: cfg.time_limit_seconds || 180, finished: false });
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo canjear el token');
      setPhase('error');
    }
  };

  const sendInteraction = async (eventType, target, extras = {}) => {
    // MVP anatómico: evaluar antes de enviar (feedback inmediato)
    if (isAnatomicalMVP && (eventType === 'xr:zone_selected' || eventType === 'xr:marker_detected')) {
      const zone = target?.zone_code || (markers.find((m) => m.marker_id === target?.marker_id)?.linked_entity_id);
      if (zone) {
        evaluateMVPSelection(zone);
        extras = { ...extras, correct: zone === currentTargetZone, target_zone: currentTargetZone };
      }
    }
    if (!session?.access_token || !session?.session_id) {
      toast.error('Sin sesión activa — sólo modo demostración');
      return;
    }
    try {
      const { data } = await axios.post(`${API_BASE}/api/xr/interaction`, {
        session_id: session.session_id,
        event_type: eventType,
        device_type: deviceType,
        target,
        ...extras,
      }, { headers: { Authorization: `Bearer ${session.access_token}` } });
      if (data?.state) setState(data.state);
      setEvents((prev) => [{ event_type: eventType, target, at: new Date().toISOString() }, ...prev].slice(0, 20));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar interacción');
    }
  };

  const refreshState = async () => {
    if (!session?.access_token || !session?.session_id) return;
    try {
      const st = await axios.get(`${API_BASE}/api/xr/sessions/${session.session_id}/state`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setState(st.data?.patient_current_state || {});
    } catch { /* noop */ }
  };

  /* ============ AR camera (QR markers) — via XRDeviceAdapter ============ */
  const startCamera = async () => {
    setCameraError('');
    try {
      const adapter = createAdapter(deviceType, { deviceName: session?.user?.email });
      adapterRef.current = adapter;
      setCameraOn(true);
      await new Promise((r) => setTimeout(r, 100));
      if (!videoRef.current) return;
      if (typeof adapter.attachVideo === 'function') adapter.attachVideo(videoRef.current);

      // Actualizamos videoFrame cuando llegan metadatos (necesario para el mapping)
      const onLoaded = () => {
        const v = videoRef.current;
        if (v && v.videoWidth) setVideoFrame({ videoWidth: v.videoWidth, videoHeight: v.videoHeight });
      };
      videoRef.current.addEventListener('loadedmetadata', onLoaded);
      videoRef.current.addEventListener('resize', onLoaded);

      await adapter.startMarkerTracking({
        onFrame: ({ marker_id, bounds }) => {
          setActiveMarker(marker_id);
          if (bounds) setMarkerBounds(bounds);
          if (videoRef.current?.videoWidth && videoFrame.videoWidth === 0) {
            setVideoFrame({ videoWidth: videoRef.current.videoWidth, videoHeight: videoRef.current.videoHeight });
          }
        },
        onLost: ({ marker_id }) => {
          setActiveMarker((cur) => (cur === marker_id ? null : cur));
          setMarkerBounds((cur) => (activeMarker === marker_id ? null : cur));
        },
        onMarker: ({ marker_id, bounds }) => {
          const match = markers.find((m) => m.marker_id === marker_id);
          setLastDetected({ marker_id, matched: !!match, at: new Date().toISOString(), label: match?.label });
          if (bounds) setMarkerBounds(bounds);
          sendInteraction('xr:marker_detected', {
            marker_id,
            marker_type: match?.marker_type || 'unknown',
            matched: !!match,
          }, { correct: !!match });
        },
        onError: (err) => setCameraError(err?.message || 'Error del adaptador'),
      });
    } catch (e) {
      setCameraError(e?.message || 'No se pudo acceder a la cámara. Requiere HTTPS y permiso del usuario.');
      setCameraOn(false);
    }
  };

  const stopCamera = async () => {
    try { if (adapterRef.current) await adapterRef.current.stopMarkerTracking(); } catch { /* noop */ }
    adapterRef.current = null;
    setCameraOn(false);
  };

  useEffect(() => () => { stopCamera(); }, []); // cleanup al desmontar

  /* ============ MVP Anatómico: timer + evaluación ============ */
  const isAnatomicalMVP = xrConfig?.mvp_type === 'anatomical_id';
  const targetSequence = xrConfig?.target_sequence || [];
  const currentTargetZone = isAnatomicalMVP && !mvpState.finished ? targetSequence[mvpState.idx] : null;

  // v10.12.0 — Cargar calibraciones cuando tenemos sesión activa
  useEffect(() => {
    if (!session?.access_token || !session?.scenario_id) return;
    axios.get(`${API_BASE}/api/xr/calibrations?scenario_id=${session.scenario_id}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).then((r) => {
      const map = {};
      for (const c of (r.data?.calibrations || [])) map[c.marker_id] = c;
      setCalibrations(map);
    }).catch(() => { /* silent */ });
  }, [session?.access_token, session?.scenario_id]);

  // Fullscreen API toggle
  const toggleFullscreen = async () => {
    const el = rootRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch { /* iOS Safari on iPhone puede no soportar */ }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Auto-arrancar cámara al llegar a la fase conectado en móvil
  useEffect(() => {
    if (phase !== 'connected' || cameraOn || cameraError) return;
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile) {
      // Auto-start after a tick — pero requiere gesto en iOS; si falla, el user pulsa Escanear
      const t = setTimeout(() => { startCamera().catch(() => { /* user has to click */ }); }, 300);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (!isAnatomicalMVP || mvpState.finished || mvpState.timeLeft <= 0) {
      if (isAnatomicalMVP && mvpState.timeLeft === 0 && !mvpState.finished) {
        setMvpState((prev) => ({ ...prev, finished: true }));
      }
      return undefined;
    }
    const t = setInterval(() => setMvpState((prev) => ({ ...prev, timeLeft: Math.max(0, prev.timeLeft - 1) })), 1000);
    return () => clearInterval(t);
  }, [isAnatomicalMVP, mvpState.finished, mvpState.timeLeft]);

  const evaluateMVPSelection = (zoneCode) => {
    if (!isAnatomicalMVP || mvpState.finished) return;
    const expected = currentTargetZone;
    const correct = zoneCode === expected;
    setMvpState((prev) => {
      const nextIdx = prev.idx + 1;
      const done = nextIdx >= targetSequence.length;
      return {
        ...prev,
        idx: correct ? nextIdx : prev.idx,
        correct: correct ? prev.correct + 1 : prev.correct,
        incorrect: correct ? prev.incorrect : prev.incorrect + 1,
        finished: done && correct,
      };
    });
    if (!correct) toast.error(`Zona esperada: ${expected}`);
    else toast.success('Correcto');
  };

  /* ============ Render por fase ============ */

  if (phase === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-cyan-900 text-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/80 border-cyan-800 backdrop-blur">
          <CardContent className="p-6 space-y-5">
            <div className="text-center">
              <Cube size={48} className="text-cyan-400 mx-auto mb-2" weight="fill" />
              <h1 className="text-xl font-bold">SimuClinic XR</h1>
              <p className="text-cyan-200/70 text-sm mt-1">Vincular dispositivo</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-cyan-300">Tipo de dispositivo</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {DEVICE_TYPES.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => setDeviceType(d.value)}
                    data-testid={`device-type-${d.value}`}
                    className={`p-3 rounded-lg border transition-all ${deviceType === d.value ? 'bg-cyan-500/30 border-cyan-400' : 'bg-slate-700/40 border-slate-600'}`}
                  >
                    <DeviceMobile size={20} className="mx-auto mb-1" />
                    <div className="text-xs">{d.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={redeem} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="redeem-btn">
              Vincular ahora
            </Button>
            <div className="pt-2 border-t border-slate-700/50 mt-2">
              <div className="text-[9px] uppercase tracking-widest text-cyan-300/70 mb-1">Dispositivo</div>
              <div className="grid grid-cols-3 gap-1 text-[10px]" data-testid="device-caps-summary">
                <div className={`p-1 rounded ${caps.camera_supported ? 'bg-emerald-900/30 text-emerald-300' : 'bg-red-900/30 text-red-300'}`}>Cámara {caps.camera_supported ? '✓' : '✗'}</div>
                <div className={`p-1 rounded ${caps.webxr_supported ? 'bg-emerald-900/30 text-emerald-300' : 'bg-slate-700/30 text-slate-500'}`}>WebXR {caps.webxr_supported ? '✓' : '—'}</div>
                <div className={`p-1 rounded ${caps.is_secure_context ? 'bg-emerald-900/30 text-emerald-300' : 'bg-red-900/30 text-red-300'}`}>HTTPS {caps.is_secure_context ? '✓' : '✗'}</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 text-center">Token: <span className="font-mono">{token?.slice(0, 12)}…</span></p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'redeeming') {
    return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">Vinculando…</div>;
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-red-900/40 border-red-800">
          <CardContent className="p-6 text-center space-y-3">
            <WarningCircle size={40} className="text-red-400 mx-auto" weight="fill" />
            <h1 className="text-lg font-bold">Error al vincular</h1>
            <p className="text-red-200 text-sm">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline" className="text-slate-900">Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connected — Fullscreen immersive layout
  const totalTargets = targetSequence.length;
  const progressPct = totalTargets ? Math.round(100 * mvpState.idx / totalTargets) : 0;
  const activeCal = activeMarker ? calibrations[activeMarker] : null;
  const timeStr = `${Math.floor(mvpState.timeLeft / 60)}:${String(mvpState.timeLeft % 60).padStart(2, '0')}`;

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 bg-black text-white overflow-hidden touch-none"
      style={{ height: '100dvh', width: '100dvw' }}
      data-testid="xr-immersive-root"
    >
      {/* Camera layer — full viewport */}
      {cameraOn ? (
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover' }}
          data-testid="xr-camera-video"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-cyan-950 p-6 text-center gap-4">
          <Cube size={56} className="text-cyan-400" weight="fill" />
          <div>
            <h1 className="text-lg font-bold" data-testid="xr-scenario-name">{scenarioName || 'SimuClinic XR'}</h1>
            <p className="text-xs text-cyan-200/70 mt-1">{session?.user?.email}</p>
          </div>
          {cameraError && (
            <div className="max-w-sm text-[11px] text-red-300 bg-red-900/30 border border-red-800 rounded p-2 flex items-start gap-1">
              <WarningCircle size={14} className="mt-0.5 shrink-0" /> {cameraError}
            </div>
          )}
          <Button onClick={startCamera} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold px-6" data-testid="ar-camera-start">
            <Camera size={16} className="mr-2" weight="fill" /> Iniciar experiencia RA
          </Button>
          <div className="text-[10px] text-slate-500 max-w-xs">
            La cámara requiere permiso del navegador y una conexión HTTPS.
          </div>
        </div>
      )}

      {/* 3D Overlay layer */}
      {cameraOn && (
        <XRMarker3DOverlay
          markers={markers}
          activeMarker={activeMarker}
          bounds={markerBounds}
          videoFrame={videoFrame}
          calibration={activeCal}
          accessToken={session?.access_token}
          debug={debugMode}
        />
      )}

      {/* Reticle (center guide) */}
      {cameraOn && !activeMarker && controlsVisible && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[45vmin] h-[45vmin] max-w-[320px] max-h-[320px] border-2 border-cyan-300/60 rounded-2xl" style={{ boxShadow: '0 0 40px rgba(34, 211, 238, 0.35)' }} />
        </div>
      )}

      {/* Top floating bar */}
      {controlsVisible && (
        <div
          className="absolute top-0 inset-x-0 z-20 pointer-events-none"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="mx-3 mt-2 flex items-center gap-2">
            <div className="pointer-events-auto flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur border border-white/10">
              <div className={`w-2 h-2 rounded-full ${push.connected ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} title={push.connected ? 'Live' : 'Off'} />
              <div className="min-w-0">
                <div className="text-[11px] font-bold truncate" data-testid="xr-scenario-name">{scenarioName || 'SimuClinic XR'}</div>
                <div className="text-[9px] text-cyan-200/80 uppercase tracking-widest truncate">{deviceType}</div>
              </div>
              {isAnatomicalMVP && !mvpState.finished && (
                <div className={`ml-auto text-sm font-mono font-bold px-2 py-0.5 rounded ${mvpState.timeLeft < 30 ? 'bg-red-500/20 text-red-300' : 'bg-cyan-500/20 text-cyan-200'}`} data-testid="xr-timer">{timeStr}</div>
              )}
            </div>

            <div className="pointer-events-auto flex items-center gap-1">
              <button onClick={() => setDebugMode((v) => !v)} className={`w-9 h-9 rounded-full backdrop-blur border ${debugMode ? 'bg-emerald-500/40 border-emerald-400 text-white' : 'bg-black/60 border-white/10 text-white/80'} flex items-center justify-center`} data-testid="xr-debug-toggle" aria-label="Debug">
                <Bug size={16} />
              </button>
              <button onClick={toggleFullscreen} className="w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center" data-testid="xr-fullscreen-toggle" aria-label={isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}>
                {isFullscreen ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
              </button>
              <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center" data-testid="xr-close-btn" aria-label="Salir">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* MVP target sub-bar */}
          {isAnatomicalMVP && !mvpState.finished && cameraOn && (
            <div className="pointer-events-auto mx-3 mt-2 px-3 py-1.5 rounded-xl bg-cyan-500/15 backdrop-blur border border-cyan-400/40" data-testid="mvp-anatomical-panel">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-cyan-300">Zona objetivo</div>
                  <div className="text-base font-bold text-white" data-testid="mvp-current-zone">{zones.find((z) => z.code === currentTargetZone)?.label || currentTargetZone || '—'}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-cyan-200">Progreso {mvpState.idx}/{totalTargets}</div>
                  <div className="text-[10px]"><span className="text-emerald-400">{mvpState.correct}</span> · <span className="text-red-400">{mvpState.incorrect}</span></div>
                </div>
              </div>
              <div className="w-full h-1 bg-slate-800 rounded overflow-hidden mt-1">
                <div className="h-full bg-cyan-400" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}

          {/* Instructor mirror badge */}
          {mirror.viewerCount > 0 && (
            <div className="pointer-events-none absolute top-2 right-32 px-2 py-0.5 rounded bg-red-500/90 text-white text-[10px] font-bold flex items-center gap-1 animate-pulse" data-testid="mirror-viewing-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> Instructor viendo
            </div>
          )}
        </div>
      )}

      {/* Vitals HUD (bottom-left, expandible) */}
      {controlsVisible && cameraOn && state && Object.keys(state).length > 0 && (
        <div
          className="absolute bottom-0 left-0 z-10 pointer-events-auto"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)' }}
        >
          <button
            onClick={() => setPtCollapsed((v) => !v)}
            className="m-3 px-3 py-2 rounded-xl bg-black/60 backdrop-blur border border-white/10 flex items-center gap-2 text-left"
            data-testid="xr-vitals-toggle"
          >
            <Heartbeat size={14} className="text-red-400" />
            {ptCollapsed ? (
              <div className="text-xs font-mono text-cyan-200">FC {state.fc ?? '—'} · SpO₂ {state.spo2 ?? '—'}</div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {['fc', 'spo2', 'pa_sistolica', 'fr'].map((k) => (
                  <div key={k} className="text-center" data-testid={`xr-vital-${k}`}>
                    <div className="text-[9px] uppercase text-slate-400">{k}</div>
                    <div className="text-base font-bold font-mono text-cyan-300">{state[k] ?? '—'}</div>
                  </div>
                ))}
              </div>
            )}
          </button>
        </div>
      )}

      {/* Bottom action bar */}
      {controlsVisible && (
        <div
          className="absolute bottom-0 inset-x-0 z-20 pointer-events-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto max-w-md m-3 flex items-center gap-2 justify-end pointer-events-none">
            {cameraOn && activeMarker && (
              <button
                onClick={() => setCalMode((v) => !v)}
                className={`pointer-events-auto h-10 px-3 rounded-full backdrop-blur border flex items-center gap-1.5 text-xs ${calMode ? 'bg-amber-500/90 border-amber-300 text-slate-900 font-bold' : 'bg-black/60 border-white/10 text-white'}`}
                data-testid="xr-cal-toggle"
              >
                <Sliders size={14} weight={calMode ? 'fill' : 'regular'} /> {calMode ? 'Cerrar' : 'Calibrar'}
              </button>
            )}
            {cameraOn ? (
              <button onClick={stopCamera} className="pointer-events-auto h-10 w-10 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center" data-testid="ar-camera-stop" aria-label="Detener cámara">
                <CameraSlash size={16} />
              </button>
            ) : (
              <button onClick={startCamera} className="pointer-events-auto h-10 px-4 rounded-full bg-cyan-500 text-slate-900 font-bold text-xs flex items-center gap-1.5" data-testid="ar-camera-start">
                <Camera size={14} weight="fill" /> Iniciar RA
              </button>
            )}
            {push.permission === 'default' && push.connected && (
              <button onClick={push.requestPermission} className="pointer-events-auto h-10 px-3 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white text-xs" data-testid="xr-push-request-btn">
                Notif ✓
              </button>
            )}
          </div>
        </div>
      )}

      {/* Toggle-controls FAB (aparece cuando los controles están ocultos) */}
      <button
        onClick={() => setControlsVisible((v) => !v)}
        className="absolute z-30 top-1/2 right-2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center"
        style={{ marginRight: 'env(safe-area-inset-right)' }}
        data-testid="xr-controls-toggle"
        aria-label={controlsVisible ? 'Ocultar controles' : 'Mostrar controles'}
      >
        {controlsVisible ? <EyeSlash size={14} /> : <Eye size={14} />}
      </button>

      {/* Calibration panel (bottom sheet flotante) */}
      {calMode && activeMarker && (
        <XRCalibrationPanel
          markerId={activeMarker}
          scenarioId={session?.scenario_id}
          accessToken={session?.access_token}
          initialCalibration={activeCal}
          onChange={(next) => setCalibrations((prev) => ({ ...prev, [activeMarker]: next }))}
          onClose={() => setCalMode(false)}
        />
      )}

      {/* Recent event ping (pequeño toast in-canvas) */}
      {lastDetected && controlsVisible && (
        <div
          className={`absolute top-24 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full backdrop-blur border text-xs flex items-center gap-1.5 ${lastDetected.matched ? 'bg-emerald-500/30 border-emerald-400 text-emerald-100' : 'bg-amber-500/30 border-amber-400 text-amber-100'}`}
          data-testid="ar-last-detected"
        >
          <Target size={12} weight="fill" />
          <span className="font-mono">{lastDetected.marker_id}</span>
          <span>{lastDetected.label || (lastDetected.matched ? 'OK' : '¿?')}</span>
        </div>
      )}
    </div>
  );
}
